import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useDispatch } from 'react-redux';
import { hasValue, isFunction } from 'swipeclock-frontend';
import { webclockApi } from 'src/services/webclock';
import { isAuthError, isTransportError } from '../utils/isNetworkError';
import { getProbeDelayMs } from '../utils/apiProbeSchedule';
import {
  getApiReachability,
  reportApiReachable,
  reportApiUnreachable,
  subscribeApiReachability,
} from '../utils/apiReachability';

const getIsApiOffline = () => getApiReachability() === 'apiOffline';

export function useIsApiOffline() {
  return useSyncExternalStore(
    subscribeApiReachability,
    getIsApiOffline,
    getIsApiOffline
  );
}

/**
 * While MobileClockConnect cannot be reached, probe on a staged schedule.
 * A successful probe marks the API online. Native reconnect and foreground
 * resume probe immediately without resetting the schedule step.
 */
export function useApiReachability({
  authBlockedRef,
  authError,
  isConnectivityOnline,
  isOfflineShellMode,
  onAuthFailure,
  user,
  userKey,
}) {
  const dispatch = useDispatch();
  const isApiOffline = useIsApiOffline();
  const attemptIndexRef = useRef(0);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const probeNowRef = useRef(() => {});
  const isConnectivityOnlineRef = useRef(isConnectivityOnline);
  const previousConnectivityRef = useRef(isConnectivityOnline);
  const isOfflineShellModeRef = useRef(isOfflineShellMode);
  const userRef = useRef(user);
  const onAuthFailureRef = useRef(onAuthFailure);

  isConnectivityOnlineRef.current = isConnectivityOnline;
  isOfflineShellModeRef.current = isOfflineShellMode;
  userRef.current = user;
  onAuthFailureRef.current = onAuthFailure;

  useEffect(() => {
    let cancelled = false;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        probeNowRef.current();
      }
    };

    const canProbe = () =>
      isOfflineShellModeRef.current &&
      hasValue(userRef.current?.token) &&
      !authBlockedRef.current &&
      isConnectivityOnlineRef.current &&
      getApiReachability() === 'apiOffline';

    const schedule = (delayMs) => {
      clearTimer();

      if (cancelled || !canProbe()) {
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        runProbe();
      }, delayMs);
    };

    const runProbe = async () => {
      if (cancelled || inFlightRef.current || !canProbe()) {
        return;
      }

      inFlightRef.current = true;
      let failed = true;

      try {
        const subscription = dispatch(
          webclockApi.endpoints.clockConnect.initiate(
            { mobile: true, user: userRef.current },
            { forceRefetch: true }
          )
        );
        const result = await subscription;

        if (isFunction(subscription?.unsubscribe)) {
          subscription.unsubscribe();
        }

        if (result?.data && !result?.error) {
          failed = false;
          reportApiReachable();
        } else if (isAuthError(result?.error)) {
          if (isFunction(onAuthFailureRef.current)) {
            onAuthFailureRef.current();
          }
          return;
        } else if (isTransportError(result?.error)) {
          reportApiUnreachable();
        }
      } catch (error) {
        if (isTransportError(error)) {
          failed = true;
          reportApiUnreachable();
        } else {
          failed = false;
        }
      } finally {
        inFlightRef.current = false;
      }

      if (cancelled || getApiReachability() !== 'apiOffline') {
        if (!cancelled) {
          attemptIndexRef.current = 0;
        }
        return;
      }

      if (failed) {
        attemptIndexRef.current += 1;
      }

      schedule(getProbeDelayMs(attemptIndexRef.current));
    };

    probeNowRef.current = () => {
      if (!canProbe() || inFlightRef.current) {
        return;
      }

      clearTimer();
      runProbe();
    };

    if (getApiReachability() === 'apiOffline') {
      schedule(getProbeDelayMs(attemptIndexRef.current));
    } else {
      attemptIndexRef.current = 0;
      clearTimer();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearTimer();
      probeNowRef.current = () => {};
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authBlockedRef, authError, dispatch, isApiOffline, userKey]);

  useEffect(() => {
    const wasOnline = previousConnectivityRef.current;
    previousConnectivityRef.current = isConnectivityOnline;

    if (!wasOnline && isConnectivityOnline) {
      probeNowRef.current();
    }
  }, [isConnectivityOnline]);

  return { isApiOffline };
}
